import { format } from "date-fns";
import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledTextarea,
} from "../../StyledCss/styledInputs";
import { ErrorText } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import {
  addDeduction,
  getSalaryEmployees,
  getALlPayslips,
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import "./index.css";

import { useForm } from "react-hook-form";

const AddDeductionForm = ({
  closeBtn,
  NotifyDeductionAdded,
  NotifyDeductionFailure,
}) => {
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    await addDeductionDetails(data);
  };

  const addDeductionDetails = async (data) => {
    setErrorMsg("");
    try {
      // Prefer the last completed month's payslip net pay — it's the most
      // accurate monthly figure (already prorated for LOP etc). Payslips
      // don't exist for the current, in-progress month, so fall back to
      // the employee's `salary` field (always available, but stored as
      // ANNUAL CTC per Settings' basic/HRA-percentage breakdown — hence
      // the /12 to get a comparable monthly figure).
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      const payslipResponse = await getALlPayslips(format(lastMonth, "yyyy-MM"));

      let monthlySalary;
      if (payslipResponse.status === 200) {
        const payslips = convertKeysToCamelCase(await payslipResponse.json());
        const employeeSalary = Array.isArray(payslips)
          ? payslips.find((emp) => emp.employeeId === data.empId)
          : undefined;
        if (employeeSalary?.netPay != null) {
          monthlySalary = Number(employeeSalary.netPay);
        }
      }

      if (monthlySalary == null) {
        const employeesResponse = await getSalaryEmployees();
        if (employeesResponse.status === 200) {
          const employeesList = employeesResponse.data?.data ?? employeesResponse.data ?? [];
          const employees = convertKeysToCamelCase(employeesList);
          const employee = Array.isArray(employees)
            ? employees.find((emp) => emp.id === data.empId)
            : undefined;
          if (employee?.salary != null) {
            monthlySalary = Number(employee.salary) / 12;
          }
        }
      }

      if (monthlySalary != null && Number(data.amount) > monthlySalary) {
        const message = "Error: Deduction amount exceeds the employee's salary amount.";
        setErrorMsg(message);
        toast.error(message);
        return;
      }

      const updatedData = {
        employee_id: data.empId,
        deduction_amount: data.amount,
        reason: data.reason,
      };

      const response = await addDeduction(updatedData);

      if (response.status === 200) {
        NotifyDeductionAdded();
        closeBtn();
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Something went wrong. Please try again."
      );
      NotifyDeductionFailure();
    }
  };

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      className="d-flex flex-column gap-3"
    >
      <div className="d-flex gap-3">
        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.empId && { borderLeft: "10px solid red" }}
              {...register("empId", {
                required: true,
                pattern: /^(?!\s)[a-zA-Z0-9]{3,10}$/,
              })}
              type="text"
              id="empId"
            />
            <StyledLabel className="user-label">Emp ID*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>

        <StyledInputGroup>
          <StyledInputContainer>
            <StyledInput
              style={errors.amount && { borderLeft: "10px solid red" }}
              {...register("amount", {
                required: true,
                pattern: /^[1-9][0-9]*$/,
              })}
              type="number"
              id="Amount"
            />
            <StyledLabel className="user-label">Amount*</StyledLabel>
          </StyledInputContainer>
        </StyledInputGroup>
      </div>

      <StyledInputGroup>
        <StyledInputContainer>
          <StyledTextarea
            style={errors.reason && { borderLeft: "10px solid red" }}
            {...register("reason", {
              required: true,
            })}
            width="100%"
            id="reason"
          />
          <StyledLabel className="user-label">Reason*</StyledLabel>
        </StyledInputContainer>
      </StyledInputGroup>

      {errorMsg && <ErrorText>*{errorMsg}</ErrorText>}

      <div className="d-flex justify-content-end gap-3">
        <SecondaryButton onClick={closeBtn} type="button">
          Cancel
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default AddDeductionForm;
